import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from './PublicNav';
import PublicFooter from './PublicFooter';
import { ff, mono } from '../styles/fonts';
import { usePageMeta } from '../hooks/usePageMeta';
import { useSchema } from '../hooks/useSchema';
import { type LabPage, buildLabJsonLd } from '../content/labPages';

export interface LabLandingPageProps {
  page: LabPage;
}

function renderCodeLines(code: string) {
  const lines = code.split('\n');
  return lines.map((line, idx) => {
    const isFaulty = line.includes('first wrong line') || line.includes('faulty line');
    return (
      <div
        key={idx}
        className={`px-3 py-1 font-mono text-xs md:text-sm whitespace-pre overflow-x-auto ${
          isFaulty
            ? 'bg-[#E8A33D]/20 text-[#fcd34d] border-l-2 border-[#E8A33D] font-semibold'
            : 'text-[#e6e1db]'
        }`}
        style={mono}
      >
        {line}
      </div>
    );
  });
}

export default function LabLandingPage({ page }: LabLandingPageProps) {
  usePageMeta(page.title, page.description, page.path);

  const schemas = useMemo(() => buildLabJsonLd(page), [page]);
  useSchema(schemas);

  return (
    <div className="bg-paper-bg text-on-surface antialiased min-h-screen flex flex-col selection:bg-primary-container/20 selection:text-primary">
      <PublicNav activePath={page.path} />

      {/* ── 1. Breadcrumb & 2. Hero ── */}
      <header className="pt-12 md:pt-16 pb-12 px-base md:px-gutter max-w-[1200px] mx-auto w-full text-center">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-8 flex justify-center">
          <ol className="flex items-center flex-wrap gap-2 text-xs md:text-sm text-on-surface-variant" style={mono}>
            <li>
              <Link
                to="/"
                className="hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#286958] focus-visible:ring-offset-2 rounded"
              >
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="text-outline-variant/60">/</li>
            {page.key === 'hub' ? (
              <li aria-current="page" className="font-semibold text-on-surface">
                {page.crumbName}
              </li>
            ) : (
              <>
                <li>
                  <Link
                    to="/labs"
                    className="hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#286958] focus-visible:ring-offset-2 rounded"
                  >
                    Labs
                  </Link>
                </li>
                <li aria-hidden="true" className="text-outline-variant/60">/</li>
                <li aria-current="page" className="font-semibold text-on-surface">
                  {page.crumbName}
                </li>
              </>
            )}
          </ol>
        </nav>

        {/* Hero Content */}
        <div className="max-w-4xl mx-auto">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-on-surface leading-[1.15] mb-6"
            style={ff}
          >
            {page.h1}
          </h1>

          <p className="text-lg md:text-xl text-on-surface-variant max-w-2xl mx-auto leading-relaxed mb-8">
            {page.subhead}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={page.ctaPath}
              className="w-full sm:w-auto px-8 py-4 rounded-full bg-[#ae3115] text-white font-semibold text-base shadow-sm hover:bg-[#922810] hover:-translate-y-0.5 transition-all motion-reduce:transform-none motion-reduce:transition-none text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#286958] focus-visible:ring-offset-2"
              style={mono}
            >
              {page.ctaLabel.toUpperCase()} &rarr;
            </Link>
          </div>
        </div>

        {/* ── 3. Quick Answer Box ── */}
        <aside
          id="quick-answer"
          aria-label="Quick Answer"
          className="mt-10 md:mt-12 max-w-3xl mx-auto bg-surface-white border border-outline-variant/30 rounded-2xl p-6 md:p-8 shadow-sm text-left"
        >
          <p className="text-xs font-bold text-[#ae3115] uppercase tracking-wider mb-2" style={mono}>
            QUICK ANSWER
          </p>
          <p className="text-base md:text-lg text-on-surface leading-relaxed">
            {page.quickAnswer}
          </p>
        </aside>
      </header>

      {/* ── 4. How It Works (Skip if steps is empty) ── */}
      {page.steps.length > 0 && (
        <section className="py-16 md:py-24 bg-surface-white border-y border-outline-variant/10 px-base md:px-gutter">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={ff}>
              {page.howToName}
            </h2>

            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 list-none p-0 m-0">
              {page.steps.map((step, idx) => (
                <li
                  key={idx}
                  className="bg-paper-bg p-6 rounded-2xl border border-outline-variant/15 flex flex-col"
                >
                  <div
                    className="w-10 h-10 rounded-xl bg-primary/10 text-primary font-bold flex items-center justify-center mb-4 text-base"
                    style={mono}
                  >
                    {idx + 1}
                  </div>
                  <h3 className="text-lg font-bold text-on-surface mb-2" style={ff}>
                    {step.name}
                  </h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">
                    {step.text}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* ── 5. Features Grid ── */}
      <section className="py-16 md:py-24 px-base md:px-gutter max-w-[1200px] mx-auto w-full">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={ff}>
          {page.featuresHeading}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {page.features.map((feature, idx) => {
            const cardInner = (
              <div
                className={`p-6 rounded-2xl bg-surface-white border border-outline-variant/20 shadow-sm h-full flex flex-col transition-all duration-200 motion-reduce:transform-none motion-reduce:transition-none ${
                  feature.path ? 'hover:-translate-y-1 hover:border-primary/40 hover:shadow-md cursor-pointer' : ''
                }`}
              >
                <h3 className="text-xl font-bold text-on-surface mb-3 flex items-center justify-between" style={ff}>
                  <span>{feature.title}</span>
                  {feature.path && (
                    <span className="text-primary text-sm font-mono font-normal ml-2" aria-hidden="true">&rarr;</span>
                  )}
                </h3>
                <p className="text-sm md:text-base text-on-surface-variant leading-relaxed">
                  {feature.body}
                </p>
              </div>
            );

            return feature.path ? (
              <Link
                key={idx}
                to={feature.path}
                className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#286958] focus-visible:ring-offset-2 rounded-2xl"
              >
                {cardInner}
              </Link>
            ) : (
              <div key={idx}>
                {cardInner}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── 6. Example Block (if present) ── */}
      {page.example && (
        <section className="py-16 md:py-24 bg-surface-white border-y border-outline-variant/10 px-base md:px-gutter">
          <div className="max-w-[900px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-3" style={ff}>
                {page.example.title}
              </h2>
              <p className="text-base md:text-lg text-on-surface-variant max-w-2xl mx-auto">
                {page.example.intro}
              </p>
            </div>

            {/* Code / Text Blocks */}
            {page.example.blocks.length > 0 && (
              <div className="space-y-6">
                {page.example.blocks.map((block, bIdx) => (
                  <div key={bIdx} className="space-y-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant" style={mono}>
                      {block.label}
                    </p>
                    {block.code && (
                      <div className="bg-[#1e1e1e] rounded-xl p-4 shadow-sm border border-black/30 overflow-hidden">
                        {renderCodeLines(block.code)}
                      </div>
                    )}
                    {block.text && (
                      <div className="p-4 rounded-xl bg-paper-bg border border-outline-variant/20 text-on-surface text-sm md:text-base leading-relaxed">
                        {block.text}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Table (if present) */}
            {page.example.table && (
              <div className="mt-8 overflow-x-auto rounded-xl border border-outline-variant/20 bg-surface-white shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-outline-variant/20 bg-surface-container-low/60">
                      {page.example.table.head.map((col, cIdx) => (
                        <th
                          key={cIdx}
                          scope="col"
                          className="p-3.5 md:p-4 text-xs font-bold text-on-surface uppercase tracking-wider"
                          style={mono}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/10 text-sm md:text-base">
                    {page.example.table.rows.map((row, rIdx) => (
                      <tr key={rIdx} className="hover:bg-surface-container-lowest transition-colors">
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx} className="p-3.5 md:p-4 text-on-surface align-top">
                            {cellIdx === 1 ? (
                              <span
                                className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20"
                                style={mono}
                              >
                                {cell}
                              </span>
                            ) : (
                              cell
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Takeaway */}
            {page.example.takeaway && (
              <div className="mt-8 p-5 rounded-xl bg-paper-bg border border-outline-variant/20">
                <p className="text-xs font-bold uppercase tracking-wider text-primary mb-1.5" style={mono}>
                  Key Takeaway
                </p>
                <p className="text-sm md:text-base text-on-surface leading-relaxed font-medium">
                  {page.example.takeaway}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── 7. Chooser Table (hub only) ── */}
      {page.chooser && page.chooser.length > 0 && (
        <section className="py-16 md:py-24 px-base md:px-gutter max-w-[1000px] mx-auto w-full">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={ff}>
              Which lab do you need?
            </h2>
            <p className="text-base md:text-lg text-on-surface-variant">
              Match your current study challenge to the right interactive lab.
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-outline-variant/20 bg-surface-white shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-outline-variant/20 bg-surface-container-low/60">
                  <th scope="col" className="p-4 md:p-5 text-xs font-bold text-on-surface uppercase tracking-wider" style={mono}>
                    YOUR SITUATION / NEED
                  </th>
                  <th scope="col" className="p-4 md:p-5 text-xs font-bold text-on-surface uppercase tracking-wider" style={mono}>
                    RECOMMENDED LAB
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10 text-sm md:text-base">
                {page.chooser.map((row, idx) => (
                  <tr key={idx} className="hover:bg-surface-container-lowest transition-colors">
                    <td className="p-4 md:p-5 text-on-surface font-medium">
                      <Link
                        to={row.path}
                        className="hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#286958] focus-visible:ring-offset-2 rounded"
                      >
                        {row.need}
                      </Link>
                    </td>
                    <td className="p-4 md:p-5">
                      <Link
                        to={row.path}
                        className="inline-flex items-center gap-1.5 text-primary font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#286958] focus-visible:ring-offset-2 rounded"
                      >
                        <span>{row.lab}</span>
                        <span aria-hidden="true">&rarr;</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── 8. FAQ Section ── */}
      <section className="py-16 md:py-24 bg-surface-white border-y border-outline-variant/10 px-base md:px-gutter">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={ff}>
            Frequently asked questions
          </h2>

          <div className="space-y-4">
            {page.faqs.map((faq, idx) => (
              <details
                key={idx}
                open
                className="group bg-paper-bg p-5 md:p-6 rounded-2xl border border-outline-variant/15 shadow-sm transition-colors"
              >
                <summary
                  className="font-bold text-lg md:text-xl text-on-surface cursor-pointer list-none flex items-center justify-between gap-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#286958] focus-visible:ring-offset-2 rounded"
                  style={ff}
                >
                  <span>{faq.q}</span>
                  <span
                    className="text-outline-variant text-sm font-mono transition-transform duration-200 group-open:rotate-180"
                    aria-hidden="true"
                  >
                    &darr;
                  </span>
                </summary>
                <p className="mt-4 text-sm md:text-base text-on-surface-variant leading-relaxed">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9. Related Links ── */}
      <section className="py-16 md:py-20 px-base md:px-gutter max-w-[900px] mx-auto w-full text-center">
        <h2 className="text-2xl md:text-3xl font-bold mb-8" style={ff}>
          Related tools & labs
        </h2>

        <div className="flex flex-wrap items-center justify-center gap-3">
          {page.related.map((rel, idx) => (
            <Link
              key={idx}
              to={rel.path}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-surface-white border border-outline-variant/20 text-on-surface hover:border-primary/40 hover:text-primary transition-all text-sm font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#286958] focus-visible:ring-offset-2 motion-reduce:transform-none motion-reduce:transition-none"
            >
              <span>{rel.label}</span>
              <span aria-hidden="true" className="text-xs">&rarr;</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── 10. Final CTA Band & Last Updated ── */}
      <section className="py-16 md:py-20 bg-surface-container-low border-t border-outline-variant/10 px-base md:px-gutter text-center">
        <div className="max-w-[700px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-on-surface mb-4" style={ff}>
            Master the reasoning behind every answer
          </h2>
          <p className="text-base md:text-lg text-on-surface-variant mb-8 max-w-xl mx-auto leading-relaxed">
            Practice the step-by-step logic exams actually test with Learnex Labs.
          </p>

          <Link
            to={page.ctaPath}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#ae3115] text-white font-semibold text-base shadow-sm hover:bg-[#922810] hover:-translate-y-0.5 transition-all motion-reduce:transform-none motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#286958] focus-visible:ring-offset-2"
            style={mono}
          >
            {page.ctaLabel.toUpperCase()} &rarr;
          </Link>

          <p className="text-xs text-on-surface-variant/70 mt-8" style={mono}>
            Last updated {page.dateModified}
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
